import { useEscapeKey } from '@/hooks/useEscapeKey';

/** Kam poslat dokument — přímo odpovídá `mode` v `generateLabelsPdf`. */
export type PrintTarget = 'dialog' | 'fronta' | 'pdf';

/**
 * Kam poslat dokument — do fronty na dílenskou tiskárnu, do tiskového
 * dialogu, nebo jen stáhnout jako PDF ke kontrole (písmo, rozložení). Ptáme
 * se pokaždé: tiskne se z tabletu i z počítače a pokaždé může jít jinam.
 * Mezi frontou a běžným tiskem se pamatuje poslední volba a je předvybraná —
 * PDF je vždycky jen jednorázová kontrola, nic si z ní nepamatujeme.
 *
 * Popisky fronty (výchozí = Zebra pro štítky role) jde přepsat přes
 * `queueLabel`/`queueDescription` pro jinou tiskárnu (např. Xerox v dílně).
 */
export function PrintTargetModal({
  title,
  heading = 'Kam vytisknout štítek?',
  vychoziFronta,
  queueLabel = 'Zebra na dílně',
  queueDescription = 'Štítek se zařadí do fronty a vytiskne se na dílně.',
  onPick,
  onClose,
}: {
  title: string;
  heading?: string;
  vychoziFronta: boolean;
  queueLabel?: string;
  queueDescription?: string;
  onPick: (target: PrintTarget) => void;
  onClose: () => void;
}) {
  useEscapeKey(onClose);

  const zvyraznit = 'ring-2 ring-primary/40';
  const tlacitko =
    'flex items-center gap-3 w-full text-left border border-border rounded-xl px-5 py-4 hover:bg-surface-hover transition-colors cursor-pointer';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 cursor-default"
      />
      <div className="relative w-full max-w-md bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text">{heading}</h2>
        <p className="text-sm text-text-secondary mt-1">{title}</p>

        <div className="mt-5 flex flex-col gap-3">
          <button
            type="button"
            onClick={() => onPick('dialog')}
            className={`${tlacitko} ${vychoziFronta ? '' : zvyraznit}`}
          >
            <span className="text-2xl">📄</span>
            <span>
              <span className="block text-base font-bold text-text">Běžný tisk</span>
              <span className="block text-sm text-text-secondary">
                Otevře se tiskový dialog tohoto zařízení.
              </span>
            </span>
          </button>

          <button
            type="button"
            onClick={() => onPick('fronta')}
            className={`${tlacitko} ${vychoziFronta ? zvyraznit : ''}`}
          >
            <span className="text-2xl">🖨️</span>
            <span>
              <span className="block text-base font-bold text-text">{queueLabel}</span>
              <span className="block text-sm text-text-secondary">{queueDescription}</span>
            </span>
          </button>

          <button type="button" onClick={() => onPick('pdf')} className={tlacitko}>
            <span className="text-2xl">🔍</span>
            <span>
              <span className="block text-base font-bold text-text">Tisk do PDF</span>
              <span className="block text-sm text-text-secondary">
                Stáhne PDF ke kontrole (písmo, rozložení) — nic se netiskne.
              </span>
            </span>
          </button>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover cursor-pointer transition-colors"
          >
            Zrušit
          </button>
        </div>
      </div>
    </div>
  );
}
